import { z } from 'zod';

/** Preferred Payment Method dropdown options. */
export const PREFERRED_PAYMENT_METHODS = ['ACH', 'Wire', 'Check', 'Virtual Card'] as const;
export type PreferredPaymentMethod = typeof PREFERRED_PAYMENT_METHODS[number];

// Mirrors the server-side `Address.SetPostalCodeAndCountryCode` check so the UI
// rejects values the backend would throw "Invalid postal code format" for.
const US_COUNTRY_VALUES = new Set(['USA', 'US', 'UNITED STATES', 'UNITED STATES OF AMERICA']);
const US_POSTAL_PATTERN = /^\d{5}(-\d{4})?$/;

function isUSCountry(country: string): boolean {
    return US_COUNTRY_VALUES.has(country.trim().toUpperCase());
}

function addPostalCodeIssue(
    ctx: z.RefinementCtx,
    path: (string | number)[],
    country: string,
    postalCode: string,
    label: string
): void {
    if (!postalCode) {
        return;
    }
    if (isUSCountry(country)) {
        if (!US_POSTAL_PATTERN.test(postalCode)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path,
                message: `${label} must be 5 digits or 5+4 format (e.g., 12345 or 12345-6789)`,
            });
        }
        return;
    }
    if (postalCode.length > 10) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path,
            message: `${label} cannot be more than 10 characters`,
        });
    }
}

/**
 * Remittance Information form.
 *
 * Fields are grouped by skeleton section in the Zod object below:
 *   - Existing HeaderInfo fields (TIN, SWIFT, IBAN, ABA, Instructions,
 *     Address, Phone, Email)
 *   - New fields (Bank*, AccountNumber, PreferredPaymentMethod,
 *     PaymentTerms, DefaultPaymentCurrency) that the backend is adding
 *     to the Field Dictionary.
 */
export const RemittanceSchema = z
    .object({
        BusinessName: z
            .string()
            .trim()
            .optional()
            .transform((val) => val ?? ''),
        TIN: z
            .union([
                z
                    .string()
                    .trim()
                    .regex(/^\d{9}$/, 'TIN must be 9 digits'),
                z.string().length(0),
            ])
            .optional()
            .transform((val) => val || undefined),
        Instructions: z
            .string()
            .trim()
            .optional()
            .transform((v) => v || undefined),
        RemittanceAddress1: z.string().trim().min(1, 'Remittance Address is required'),
        RemittanceCity: z.string().trim().min(1, 'City is required'),
        RemittanceState: z.string().trim().min(1, 'State is required'),
        RemittancePostalCode: z.string().trim().min(1, 'Postal Code is required'),
        RemittanceCountry: z.string().trim().min(1, 'Country is required'),
        RemittancePhone: z.string().trim().min(1, 'Phone is required'),
        RemittanceEmail: z.string().trim().min(1, 'Email is required').email('Must be a valid email'),

        BankName: z.string().trim().min(1, 'Bank Name is required'),
        BankAddress1: z.string().trim().min(1, 'Bank Street Address is required'),
        BankCity: z.string().trim().min(1, 'Bank City is required'),
        BankState: z.string().trim().min(1, 'Bank State is required'),
        BankPostalCode: z.string().trim().min(1, 'Bank Postal Code is required'),
        BankCountry: z.string().trim().min(1, 'Bank Country is required'),
        SWIFT: z
            .union([
                z
                    .string()
                    .trim()
                    .regex(/^[A-Za-z0-9]{8}([A-Za-z0-9]{3})?$/, 'SWIFT/BIC must be 8 or 11 alphanumeric characters'),
                z.string().length(0),
            ])
            .optional()
            .transform((val) => val || undefined),
        IBAN: z
            .union([
                z
                    .string()
                    .trim()
                    .regex(/^[A-Za-z0-9]+$/, 'IBAN may only contain letters and numbers'),
                z.string().length(0),
            ])
            .optional()
            .transform((val) => val || undefined),
        ABARoutingNumber: z
            .union([
                z
                    .string()
                    .trim()
                    .regex(/^\d{9}$/, 'ABA Routing Number must be 9 digits'),
                z.string().length(0),
            ])
            .optional()
            .transform((val) => val || undefined),
        AccountNumber: z
            .union([
                z
                    .string()
                    .trim()
                    .regex(/^[A-Za-z0-9-]+$/, 'Account Number may only contain letters, numbers, and dashes'),
                z.string().length(0),
            ])
            .optional()
            .transform((val) => val || undefined),
        SortCode: z
            .union([
                z
                    .string()
                    .trim()
                    .regex(/^\d{2}([- ]?)\d{2}\1\d{2}$/, 'Sort Code must be 6 digits separated by a space or dash'),
                z.string().length(0),
            ])
            .optional()
            .transform((val) => val || undefined),
        PreferredPaymentMethod: z.string().trim().min(1, 'Payment Method is required'),
        PaymentTerms: z.string().trim().min(1, 'Payment Terms are required'),
        DefaultPaymentCurrency: z.string().trim().min(1, 'Default Payment Currency is required'),
    })
    .superRefine((values, ctx) => {
        if (values.TIN && !values.BusinessName) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['BusinessName'],
                message: 'Business Name is required when TIN is provided',
            });
        }

        addPostalCodeIssue(
            ctx,
            ['RemittancePostalCode'],
            values.RemittanceCountry,
            values.RemittancePostalCode.trim(),
            'Postal Code'
        );
        addPostalCodeIssue(
            ctx,
            ['BankPostalCode'],
            values.BankCountry,
            values.BankPostalCode.trim(),
            'Bank Postal Code'
        );

        if (values.AccountNumber && !values.ABARoutingNumber) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['ABARoutingNumber'],
                message: 'ABA Routing Number is required if Account Number is filled out',
            });
        }

        if (values.ABARoutingNumber && !values.AccountNumber) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['AccountNumber'],
                message: 'Account Number is required if ABA Routing Number is filled out',
            });
        }
    });

export type RemittanceFormViewModel = z.infer<typeof RemittanceSchema>;

export const emptyRemittanceFormViewModel: RemittanceFormViewModel = {
    BusinessName: '',
    TIN: '',
    SWIFT: '',
    IBAN: '',
    ABARoutingNumber: '',
    Instructions: '',

    RemittanceAddress1: '',
    RemittanceCity: '',
    RemittanceState: '',
    RemittancePostalCode: '',
    RemittanceCountry: '',
    RemittancePhone: '',
    RemittanceEmail: '',

    BankName: '',
    BankAddress1: '',
    BankCity: '',
    BankState: '',
    BankPostalCode: '',
    BankCountry: '',
    AccountNumber: '',

    PreferredPaymentMethod: '',
    PaymentTerms: '',
    DefaultPaymentCurrency: '',
};
